import { useSearchParams } from "react-router-dom";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import client from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Save, Plus, Trash2, GripVertical, CheckCircle2, XCircle, Sparkles, ChevronDown, Clock, ArrowRight, RotateCcw, Download, Type, Globe2, Tags, Layers, Wallet, Workflow, ShieldCheck, FileDown } from "lucide-react";
import { CloudIcon, ProviderIcon } from "@/components/brand-icons";
import { cn } from "@/lib/utils";
import { ListInput } from "@/components/list-input";
import { CLOUD_REGIONS } from "@/lib/regions";

const STYLE_SEGMENTS = ["prefix", "project", "resource", "env", "region"];
const SEGMENT_LABELS = {
  prefix: { title: "Prefix", hint: "enforced prefix" },
  project: { title: "Project", hint: "user project name" },
  resource: { title: "Resource", hint: "e.g. sa, kv, app" },
  env: { title: "Env", hint: "environment tag" },
  region: { title: "Region", hint: "deployment region" },
};

const PRESETS = [
  {
    id: "toastern",
    label: "Toastern default",
    hint: "prefix · project · resource · env",
    rules: {
      naming_pattern: "^[a-z][a-z0-9-]{2,40}$",
      naming_prefix: "cor-",
      naming_separator: "-",
      naming_max_length: 40,
      naming_style: ["prefix", "project", "resource", "env"],
    },
  },
  {
    id: "cloud-shortname-env",
    label: "Cloud-shortname-env",
    hint: "resource · project · env",
    rules: {
      naming_pattern: "^[a-z][a-z0-9-]{1,40}$",
      naming_prefix: "",
      naming_separator: "-",
      naming_max_length: 40,
      naming_style: ["resource", "project", "env"],
    },
  },
  {
    id: "biz-project-region",
    label: "Biz-project-region",
    hint: "prefix · project · region",
    rules: {
      naming_pattern: "^[a-z][a-z0-9-]{2,40}$",
      naming_prefix: "biz-",
      naming_separator: "-",
      naming_max_length: 40,
      naming_style: ["prefix", "project", "region"],
    },
  },
  {
    id: "landing-zone",
    label: "Azure landing zone",
    hint: "resource · project · env · region",
    rules: {
      naming_pattern: "^[a-z][a-z0-9-]{2,60}$",
      naming_prefix: "",
      naming_separator: "-",
      naming_max_length: 60,
      naming_style: ["resource", "project", "env", "region"],
    },
  },
  {
    id: "compact-underscore",
    label: "Compact underscore",
    hint: "prefix · project · resource   (underscore)",
    rules: {
      naming_pattern: "^[a-z][a-z0-9_]{2,40}$",
      naming_prefix: "cor_",
      naming_separator: "_",
      naming_max_length: 40,
      naming_style: ["prefix", "project", "resource"],
    },
  },
];

function detectActivePreset(rules) {
  if (!rules) return null;
  return PRESETS.find((p) => {
    const r = p.rules;
    return (
      r.naming_pattern === rules.naming_pattern &&
      r.naming_prefix === rules.naming_prefix &&
      r.naming_separator === rules.naming_separator &&
      Number(r.naming_max_length) === Number(rules.naming_max_length) &&
      Array.isArray(rules.naming_style) &&
      r.naming_style.length === rules.naming_style.length &&
      r.naming_style.every((s, i) => s === rules.naming_style[i])
    );
  })?.id || null;
}

function ChangedBadge() {
  return (
    <span
      className="ml-2 inline-flex items-center rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0.5 font-mono-tight text-[9px] uppercase tracking-widest text-amber-700"
      data-testid="changed-badge"
    >
      changed
    </span>
  );
}

function isChanged(draft, source, key) {
  if (!source) return false;
  return JSON.stringify(draft?.[key]) !== JSON.stringify(source?.[key]);
}

function SectionSave({ dirty, onSave, saving, testid }) {
  return (
    <Button
      type="button"
      onClick={onSave}
      disabled={!dirty || saving}
      className={cn(
        "h-9 rounded-md",
        dirty ? "bg-slate-900 text-white hover:bg-slate-800" : "bg-slate-200 text-slate-600 hover:bg-slate-200",
      )}
      data-testid={testid}
    >
      <Save className="mr-2 h-3.5 w-3.5" />
      {saving ? "Saving…" : dirty ? "Save section" : "Saved"}
    </Button>
  );
}

function StyleReorder({ value = [], onChange, readOnly }) {
  const active = value.filter((s) => STYLE_SEGMENTS.includes(s));
  const inactive = STYLE_SEGMENTS.filter((s) => !active.includes(s));
  const [dragging, setDragging] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  const add = (s) => onChange([...active, s]);
  const remove = (s) => onChange(active.filter((x) => x !== s));

  const onDragStart = (e, s) => {
    if (readOnly) return;
    setDragging(s);
    e.dataTransfer.effectAllowed = "move";
    try {
      e.dataTransfer.setData("text/plain", s);
    } catch (err) {
      console.debug("dataTransfer.setData unsupported", err);
    }
  };
  const onDragOverSeg = (e, s) => {
    if (readOnly || !dragging) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOver(s);
  };
  const onDropSeg = (e, target) => {
    if (readOnly || !dragging) return;
    e.preventDefault();
    if (dragging === target) { setDragging(null); setDragOver(null); return; }
    const from = active.indexOf(dragging);
    const to = active.indexOf(target);
    if (from < 0 || to < 0) { setDragging(null); setDragOver(null); return; }
    const arr = [...active];
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    onChange(arr);
    setDragging(null);
    setDragOver(null);
  };
  const onDragEnd = () => { setDragging(null); setDragOver(null); };

  return (
    <div data-testid="style-reorder">
      <div className="flex flex-wrap items-center gap-2" onDragOver={(e) => e.preventDefault()}>
        {active.map((s) => (
          <div
            key={s}
            data-testid={`style-segment-${s}`}
            draggable={!readOnly}
            onDragStart={(e) => onDragStart(e, s)}
            onDragOver={(e) => onDragOverSeg(e, s)}
            onDragLeave={() => setDragOver((v) => (v === s ? null : v))}
            onDrop={(e) => onDropSeg(e, s)}
            onDragEnd={onDragEnd}
            className={cn(
              "group flex select-none items-center gap-0 overflow-hidden rounded-md border transition-all",
              dragging === s ? "opacity-40" : "opacity-100",
              dragOver === s && dragging !== s ? "ring-2 ring-emerald-400 ring-offset-2 ring-offset-white" : "",
              "border-slate-900 bg-slate-900 text-white",
              readOnly ? "cursor-default" : "cursor-grab active:cursor-grabbing",
            )}
          >
            {!readOnly && (
              <div className="grid h-full place-items-center border-r border-slate-700 px-1.5 text-slate-400" aria-hidden>
                <GripVertical className="h-3.5 w-3.5" />
              </div>
            )}
            <div className="flex flex-col px-3 py-1.5">
              <span className="font-display text-sm font-semibold leading-tight">{SEGMENT_LABELS[s].title}</span>
              <span className="font-mono-tight text-[9px] uppercase tracking-widest text-slate-400">{SEGMENT_LABELS[s].hint}</span>
            </div>
            {!readOnly && (
              <button
                type="button"
                onClick={() => remove(s)}
                className="h-full border-l border-slate-700 px-2 text-slate-300 transition-colors hover:bg-slate-800"
                data-testid={`style-remove-${s}`}
                aria-label={`Remove ${s}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ))}
        {active.length === 0 && (
          <span className="font-mono-tight text-xs text-slate-500">No segments — add one on the right.</span>
        )}
      </div>
      {!readOnly && (
        <p className="mt-3 font-mono-tight text-[10px] uppercase tracking-widest text-slate-500">
          drag &amp; drop to reorder
        </p>
      )}
      {!readOnly && inactive.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="font-mono-tight text-[10px] uppercase tracking-widest text-slate-500">// available</span>
          {inactive.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => add(s)}
              className="inline-flex items-center gap-2 rounded-md border border-dashed border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-600 transition-colors hover:border-slate-500 hover:text-slate-900"
              data-testid={`style-add-${s}`}
            >
              <Plus className="h-3.5 w-3.5" />
              <span className="font-display text-sm font-semibold">{SEGMENT_LABELS[s].title}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function LivePreview({ draft }) {
  const [preview, setPreview] = useState(null);
  const [projectName, setProjectName] = useState("myapp");
  const [env, setEnv] = useState("dev");
  const [region, setRegion] = useState("weu");
  const [resource, setResource] = useState("azure_storage_account");

  useEffect(() => {
    const id = setTimeout(async () => {
      try {
        const { data } = await client.post("/governance/preview", {
          project_name: projectName,
          env,
          region,
          resource_key: resource,
          rules: draft,
        });
        setPreview(data);
      } catch (e) {
        setPreview(null);
      }
    }, 250);
    return () => clearTimeout(id);
  }, [draft, projectName, env, region, resource]);

  return (
    <div className="mt-6 rounded-md border border-slate-200 bg-slate-950 text-slate-200" data-testid="preview-panel">
      <div className="border-b border-slate-800 px-5 py-3 font-mono-tight text-[11px] uppercase tracking-widest text-slate-500">
        // live preview
      </div>
      <div className="grid grid-cols-2 gap-3 border-b border-slate-800 p-5 sm:grid-cols-4">
        <div>
          <label className="font-mono-tight text-[10px] uppercase tracking-widest text-slate-500">Project</label>
          <Input data-testid="preview-project" value={projectName} onChange={(e) => setProjectName(e.target.value)} className="mt-1 h-8 border-slate-700 bg-slate-900 font-mono-tight text-xs text-slate-100" />
        </div>
        <div>
          <label className="font-mono-tight text-[10px] uppercase tracking-widest text-slate-500">Env</label>
          <Input data-testid="preview-env" value={env} onChange={(e) => setEnv(e.target.value)} className="mt-1 h-8 border-slate-700 bg-slate-900 font-mono-tight text-xs text-slate-100" />
        </div>
        <div>
          <label className="font-mono-tight text-[10px] uppercase tracking-widest text-slate-500">Region</label>
          <Input data-testid="preview-region" value={region} onChange={(e) => setRegion(e.target.value)} className="mt-1 h-8 border-slate-700 bg-slate-900 font-mono-tight text-xs text-slate-100" />
        </div>
        <div>
          <label className="font-mono-tight text-[10px] uppercase tracking-widest text-slate-500">Resource</label>
          <Select value={resource} onValueChange={setResource}>
            <SelectTrigger data-testid="preview-resource" className="mt-1 h-8 border-slate-700 bg-slate-900 font-mono-tight text-xs text-slate-100"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="azure_storage_account">azure_storage_account</SelectItem>
              <SelectItem value="azure_key_vault">azure_key_vault</SelectItem>
              <SelectItem value="azure_app_service">azure_app_service</SelectItem>
              <SelectItem value="azure_sql_database">azure_sql_database</SelectItem>
              <SelectItem value="azure_virtual_network">azure_virtual_network</SelectItem>
              <SelectItem value="aws_s3_bucket">aws_s3_bucket</SelectItem>
              <SelectItem value="aws_dynamodb_table">aws_dynamodb_table</SelectItem>
              <SelectItem value="aws_lambda_function">aws_lambda_function</SelectItem>
              <SelectItem value="aws_vpc">aws_vpc</SelectItem>
              <SelectItem value="aws_rds_instance">aws_rds_instance</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="p-5" data-testid="preview-output">
        {preview ? (
          <>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <span className="font-mono-tight text-[10px] uppercase tracking-widest text-slate-500">generated name</span>
              {preview.matches_pattern ? (
                <span className="inline-flex items-center gap-1 text-emerald-400"><CheckCircle2 className="h-3.5 w-3.5" /> matches</span>
              ) : (
                <span className="inline-flex items-center gap-1 text-amber-400"><XCircle className="h-3.5 w-3.5" /> pattern fail</span>
              )}
            </div>
            <div className="rounded-sm bg-slate-900/70 px-3 py-2 font-mono-tight text-sm text-emerald-300" data-testid="preview-sample">
              {preview.sample}
            </div>
            <div className="mt-3 grid grid-cols-3 gap-3 font-mono-tight text-[11px] text-slate-400">
              <span>length: <span className="text-slate-200">{preview.length}</span></span>
              <span>separator: <span className="text-slate-200">{preview.effective_separator || "∅"}</span></span>
              <span>max: <span className="text-slate-200">{preview.effective_max_length}</span></span>
            </div>
          </>
        ) : (
          <p className="font-mono-tight text-xs text-slate-500">Awaiting draft…</p>
        )}
      </div>
    </div>
  );
}

// --- Section drafts ---
// Each card owns a local draft of its fields, seeded from the workspace rules.
// Saving PATCHes only the fields it owns. The parent Rules component holds
// the source-of-truth `rules` object and refreshes it after each save.

// Lets each section report its unsaved state to the Governance pane (null outside the page).
const SectionDirtyContext = createContext(null);

function useSectionDraft(source, keys) {
  const keySig = keys.join("|");
  const [draft, setDraft] = useState(() => Object.fromEntries(keys.map((k) => [k, source?.[k]])));
  useEffect(() => {
    setDraft(Object.fromEntries(keySig.split("|").map((k) => [k, source?.[k]])));
  }, [source, keySig]);
  const dirty = useMemo(() => {
    if (!source) return false;
    return keys.some((k) => JSON.stringify(draft[k]) !== JSON.stringify(source[k]));
  }, [draft, source, keys]);
  const reportDirty = useContext(SectionDirtyContext);
  useEffect(() => {
    reportDirty?.(dirty);
  }, [dirty, reportDirty]);
  return { draft, setDraft, dirty };
}

function NamingSection({ rules, onSaved, readOnly }) {
  const keys = ["naming_pattern", "naming_prefix", "naming_separator", "naming_max_length", "naming_style", "naming_exceptions"];
  const { draft, setDraft, dirty } = useSectionDraft(rules, keys);
  const [saving, setSaving] = useState(false);
  const [presetsOpen, setPresetsOpen] = useState(false);

  const activePresetId = detectActivePreset(draft);
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const exceptions = draft.naming_exceptions || [];
  const setExc = (i, k, v) => set("naming_exceptions", exceptions.map((e, idx) => idx === i ? { ...e, [k]: v } : e));
  const addExc = () => set("naming_exceptions", [...exceptions, { resource_key: "", separator: "", max_length: 24 }]);
  const rmExc = (i) => set("naming_exceptions", exceptions.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        naming_pattern: draft.naming_pattern,
        naming_prefix: draft.naming_prefix,
        naming_separator: draft.naming_separator ?? "",
        naming_max_length: Number(draft.naming_max_length) || 0,
        naming_style: (draft.naming_style || []).filter((s) => STYLE_SEGMENTS.includes(s)),
        naming_exceptions: (draft.naming_exceptions || [])
          .filter((e) => e.resource_key)
          .map((e) => ({
            resource_key: e.resource_key,
            separator: e.separator ?? "",
            max_length: Number(e.max_length) || 0,
          })),
      };
      const { data } = await client.patch("/governance/rules", payload);
      toast.success("Naming saved");
      onSaved(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const draftPayloadForPreview = useMemo(() => ({
    ...rules,
    ...draft,
  }), [rules, draft]);

  return (
    <Card className="rounded-md border border-slate-200 bg-white p-6 shadow-none lg:col-span-2">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold">Naming</h2>
          <p className="mt-1 text-sm text-slate-500">
            The regex, prefix, separator, style and length limits every project name (and derived resource name) must follow.
          </p>
        </div>
        {!readOnly && <SectionSave dirty={dirty} onSave={save} saving={saving} testid="save-naming" />}
      </div>

      {/* Presets */}
      <div className="mt-5 overflow-hidden rounded-md border border-slate-200 bg-slate-50/60" data-testid="preset-block">
        <button
          type="button"
          onClick={() => setPresetsOpen(!presetsOpen)}
          className="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-slate-100"
          data-testid="preset-toggle"
          aria-expanded={presetsOpen}
        >
          <div className="flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5 text-slate-400" />
            <span className="font-mono-tight text-[10px] uppercase tracking-widest text-slate-500">// presets</span>
            {activePresetId && (
              <Badge className="ml-2 rounded-full border border-emerald-200 bg-emerald-50 text-[10px] uppercase tracking-widest text-emerald-700" data-testid="active-preset-badge">
                on · {PRESETS.find((p) => p.id === activePresetId)?.label}
              </Badge>
            )}
            {!activePresetId && (
              <span className="ml-2 font-mono-tight text-[10px] uppercase tracking-widest text-slate-400">custom rules</span>
            )}
          </div>
          <ChevronDown className={cn("h-4 w-4 text-slate-500 transition-transform", presetsOpen && "rotate-180")} />
        </button>
        {presetsOpen && (
          <div className="border-t border-slate-200 p-4" data-testid="preset-content">
            <div className="flex flex-wrap gap-2">
              {PRESETS.map((p) => {
                const isActive = activePresetId === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    disabled={readOnly}
                    onClick={() => setDraft((d) => ({ ...d, ...p.rules }))}
                    className={cn(
                      "group inline-flex flex-col items-start gap-0.5 rounded-md border px-3 py-2 text-left transition-colors",
                      isActive
                        ? "border-emerald-500 bg-emerald-50 text-slate-900 ring-2 ring-emerald-400 ring-offset-2 ring-offset-white"
                        : "border-slate-200 bg-white hover:border-slate-900 hover:bg-slate-900 hover:text-white",
                      readOnly && "cursor-default opacity-70",
                    )}
                    data-testid={`preset-${p.id}`}
                    data-active={isActive ? "true" : "false"}
                  >
                    <span className="flex items-center gap-2 font-display text-sm font-semibold">
                      {p.label}
                      {isActive && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}
                    </span>
                    <span className={cn("font-mono-tight text-[10px] uppercase tracking-widest", isActive ? "text-emerald-700" : "text-slate-500 group-hover:text-slate-400")}>
                      {p.hint}
                    </span>
                  </button>
                );
              })}
            </div>
            <p className="mt-3 font-mono-tight text-[10px] text-slate-400">Click a preset to fill pattern, prefix, separator, style and max length. Then click "Save section".</p>
          </div>
        )}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <Label className="flex items-center text-xs uppercase tracking-widest text-slate-500">
            Regex pattern {isChanged(draft, rules, "naming_pattern") && <ChangedBadge />}
          </Label>
          <Input data-testid="rule-pattern" readOnly={readOnly} className="mt-2 h-10 border-slate-200 font-mono-tight text-sm" value={draft.naming_pattern || ""} onChange={(e) => set("naming_pattern", e.target.value)} />
        </div>
        <div>
          <Label className="flex items-center text-xs uppercase tracking-widest text-slate-500">
            Enforced prefix {isChanged(draft, rules, "naming_prefix") && <ChangedBadge />}
          </Label>
          <Input data-testid="rule-prefix" readOnly={readOnly} className="mt-2 h-10 border-slate-200 font-mono-tight text-sm" value={draft.naming_prefix || ""} onChange={(e) => set("naming_prefix", e.target.value)} />
        </div>
        <div>
          <Label className="flex items-center text-xs uppercase tracking-widest text-slate-500">
            Separator {isChanged(draft, rules, "naming_separator") && <ChangedBadge />}
          </Label>
          <Input data-testid="rule-separator" readOnly={readOnly} className="mt-2 h-10 border-slate-200 font-mono-tight text-sm" value={draft.naming_separator ?? ""} onChange={(e) => set("naming_separator", e.target.value)} placeholder="- (leave empty for none)" maxLength={3} />
        </div>
        <div>
          <Label className="flex items-center text-xs uppercase tracking-widest text-slate-500">
            Max characters {isChanged(draft, rules, "naming_max_length") && <ChangedBadge />}
          </Label>
          <Input data-testid="rule-maxlen" readOnly={readOnly} type="number" min={1} max={200} className="mt-2 h-10 border-slate-200 font-mono-tight text-sm" value={draft.naming_max_length ?? 0} onChange={(e) => set("naming_max_length", Number(e.target.value))} />
        </div>
      </div>

      {/* Style */}
      <div className="mt-8 rounded-md border border-slate-200 bg-white p-5" data-testid="style-block">
        <div>
          <p className="font-mono-tight text-[11px] uppercase tracking-widest text-slate-500">// style</p>
          <h3 className="mt-1 flex items-center font-display text-base font-semibold">
            Segment order {isChanged(draft, rules, "naming_style") && <ChangedBadge />}
          </h3>
          <p className="mt-1 text-xs text-slate-500">
            Reorder the segments used to build every resource name.
          </p>
        </div>
        <div className="mt-4">
          <StyleReorder value={draft.naming_style || []} onChange={(v) => set("naming_style", v)} readOnly={readOnly} />
        </div>
      </div>

      <LivePreview draft={draftPayloadForPreview} />

      {/* Exceptions */}
      <div className="mt-8 rounded-md border border-dashed border-slate-200 bg-slate-50/60 p-5" data-testid="exceptions-block">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <p className="font-mono-tight text-[11px] uppercase tracking-widest text-slate-500">// exceptions</p>
            <h3 className="mt-1 font-display text-base font-semibold">Per-resource naming exceptions</h3>
            <p className="mt-1 text-xs text-slate-500">
              Override the workspace separator or max length for specific resources.
            </p>
          </div>
          {!readOnly && (
            <Button variant="outline" size="sm" onClick={addExc} className="h-9 border-slate-300 text-slate-700" data-testid="add-exception">
              <Plus className="mr-1 h-3.5 w-3.5" /> Add exception
            </Button>
          )}
        </div>

        {exceptions.length === 0 ? (
          <p className="mt-3 font-mono-tight text-xs text-slate-500">No exception defined. The workspace defaults apply to every resource.</p>
        ) : (
          <div className="mt-4 space-y-2">
            <div className="hidden grid-cols-[minmax(0,1fr)_140px_140px_40px] gap-3 px-2 pb-1 font-mono-tight text-[10px] uppercase tracking-widest text-slate-500 md:grid">
              <span>Resource</span><span>Separator</span><span>Max length</span><span></span>
            </div>
            {exceptions.map((exc, i) => (
              <ExceptionRow
                key={exc.resource_key || `new-${i}`}
                exc={exc}
                index={i}
                readOnly={readOnly}
                onChange={(k, v) => setExc(i, k, v)}
                onRemove={() => rmExc(i)}
              />
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

function ExceptionRow({ exc, index, readOnly, onChange, onRemove }) {
  const [catalog, setCatalog] = useState([]);
  useEffect(() => {
    client.get("/catalog/resources").then((r) => setCatalog(r.data)).catch(() => {});
  }, []);
  return (
    <div className="grid grid-cols-1 items-center gap-2 rounded-md border border-slate-200 bg-white p-3 md:grid-cols-[minmax(0,1fr)_140px_140px_40px] md:gap-3 md:p-2" data-testid={`exception-row-${index}`}>
      <Select value={exc.resource_key} onValueChange={(v) => onChange("resource_key", v)} disabled={readOnly}>
        <SelectTrigger data-testid={`exception-resource-${index}`} className="h-9 border-slate-200 font-mono-tight text-xs">
          <SelectValue placeholder="pick a resource" />
        </SelectTrigger>
        <SelectContent>
          {catalog.map((r) => (
            <SelectItem key={r.key} value={r.key}>
              <span className="font-mono-tight text-xs">{r.key}</span>
              <span className="ml-2 text-[10px] uppercase tracking-widest text-slate-500">{r.cloud}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input data-testid={`exception-sep-${index}`} readOnly={readOnly} className="h-9 border-slate-200 font-mono-tight text-sm" value={exc.separator ?? ""} onChange={(e) => onChange("separator", e.target.value)} placeholder="(none)" maxLength={3} />
      <Input data-testid={`exception-max-${index}`} readOnly={readOnly} type="number" min={1} max={200} className="h-9 border-slate-200 font-mono-tight text-sm" value={exc.max_length ?? 0} onChange={(e) => onChange("max_length", Number(e.target.value))} />
      {!readOnly && (
        <Button variant="ghost" size="icon" onClick={onRemove} data-testid={`exception-remove-${index}`}>
          <Trash2 className="h-4 w-4 text-slate-500" />
        </Button>
      )}
    </div>
  );
}

function RegionsSection({ rules, onSaved, readOnly }) {
  const { draft, setDraft, dirty } = useSectionDraft(rules, ["allowed_regions"]);
  const [saving, setSaving] = useState(false);
  const setRegion = (cloud, list) => setDraft((d) => ({
    ...d,
    allowed_regions: { ...(d.allowed_regions || {}), [cloud]: list },
  }));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await client.patch("/governance/rules", { allowed_regions: draft.allowed_regions });
      toast.success("Regions saved");
      onSaved(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Card className="rounded-md border border-slate-200 bg-white p-6 shadow-none">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold">Regions</h2>
          <p className="mt-1 text-sm text-slate-500">Whitelist per cloud — pick real regions from the list or type a custom one.</p>
        </div>
        {!readOnly && <SectionSave dirty={dirty} onSave={save} saving={saving} testid="save-regions" />}
      </div>
      <div className="mt-6 space-y-4">
        {Object.keys(draft.allowed_regions || {}).map((c) => (
          <div key={c}>
            <Label className="text-xs uppercase tracking-widest text-slate-500">{c}</Label>
            <ListInput
              testid={`rule-region-${c}`}
              readOnly={readOnly}
              className="mt-2"
              placeholder={`search ${c} regions…`}
              options={CLOUD_REGIONS[c] || []}
              value={draft.allowed_regions?.[c] || []}
              onChange={(list) => setRegion(c, list)}
            />
          </div>
        ))}
      </div>
    </Card>
  );
}

function TagsSection({ rules, onSaved, readOnly }) {
  const { draft, setDraft, dirty } = useSectionDraft(rules, ["mandatory_tags"]);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await client.patch("/governance/rules", { mandatory_tags: draft.mandatory_tags });
      toast.success("Mandatory tags saved");
      onSaved(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Card className="rounded-md border border-slate-200 bg-white p-6 shadow-none">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold">Mandatory tags</h2>
          <p className="mt-1 text-sm text-slate-500">Every resource must carry these tags — type a tag, then press Enter or comma.</p>
        </div>
        {!readOnly && <SectionSave dirty={dirty} onSave={save} saving={saving} testid="save-tags" />}
      </div>
      <div className="mt-6">
        <ListInput
          testid="rule-tags"
          readOnly={readOnly}
          placeholder="e.g. owner"
          value={draft.mandatory_tags || []}
          onChange={(list) => setDraft((d) => ({ ...d, mandatory_tags: list }))}
        />
      </div>
    </Card>
  );
}

const CI_PLATFORMS = [
  { key: "github", label: "GitHub Actions" },
  { key: "gitlab", label: "GitLab CI" },
  { key: "azure_devops", label: "Azure Pipelines" },
  { key: "none", label: "No pipeline" },
];
const STATE_FIELDS = {
  azure: [["resource_group", "Resource group"], ["storage_account", "Storage account"], ["container", "Container"]],
  aws: [["bucket", "S3 bucket"], ["region", "Bucket region"], ["dynamodb_table", "DynamoDB lock table"]],
  gcp: [["bucket", "GCS bucket"], ["prefix", "Prefix"]],
};

function PipelineSection({ rules, onSaved, readOnly }) {
  const { draft, setDraft, dirty } = useSectionDraft(rules, ["ci_platform", "state_backend"]);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await client.patch("/governance/rules", { ci_platform: draft.ci_platform, state_backend: draft.state_backend });
      toast.success("Pipeline & state backend saved");
      onSaved(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };
  const setState = (cloud, field, v) => setDraft((d) => ({
    ...d,
    state_backend: { ...(d.state_backend || {}), [cloud]: { ...((d.state_backend || {})[cloud] || {}), [field]: v } },
  }));
  return (
    <Card className="rounded-md border border-slate-200 bg-white p-6 shadow-none lg:col-span-2" data-testid="pipeline-section">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold">CI/CD pipeline & remote state</h2>
          <p className="mt-1 text-sm text-slate-500">Default pipeline added to generated projects (with the Toastern compliance gate) and the Terraform state backend per cloud.</p>
        </div>
        {!readOnly && <SectionSave dirty={dirty} onSave={save} saving={saving} testid="save-pipeline" />}
      </div>
      <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[260px_1fr]">
        <div>
          <Label className="text-xs uppercase tracking-widest text-slate-500">Default CI platform</Label>
          <div className="mt-2 space-y-2">
            {CI_PLATFORMS.map((p) => (
              <button
                key={p.key}
                type="button"
                disabled={readOnly}
                onClick={() => setDraft((d) => ({ ...d, ci_platform: p.key }))}
                className={cn(
                  "flex w-full items-center gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors disabled:cursor-default",
                  draft.ci_platform === p.key ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 hover:border-slate-400",
                )}
                data-testid={`ci-platform-${p.key}`}
              >
                {p.key !== "none" && <ProviderIcon provider={p.key} className="h-4 w-4" />}
                <span className="font-medium">{p.label}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-5">
          {Object.entries(STATE_FIELDS).map(([cloud, fields]) => (
            <div key={cloud}>
              <div className="flex items-center gap-2">
                <CloudIcon cloud={cloud} className="h-4 w-4" />
                <Label className="text-xs uppercase tracking-widest text-slate-500">{cloud} state backend</Label>
              </div>
              <div className={cn("mt-2 grid gap-3", fields.length === 3 ? "grid-cols-3" : "grid-cols-2")}>
                {fields.map(([field, label]) => (
                  <div key={field}>
                    <span className="font-mono-tight text-[10px] uppercase tracking-widest text-slate-400">{label}</span>
                    <Input
                      readOnly={readOnly}
                      data-testid={`state-${cloud}-${field}`}
                      className="mt-1 h-9 border-slate-200 font-mono-tight text-xs"
                      placeholder={rules?.state_backend_effective?.[cloud]?.[field] || ""}
                      value={draft.state_backend?.[cloud]?.[field] ?? ""}
                      onChange={(e) => setState(cloud, field, e.target.value)}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
          <p className="font-mono-tight text-[11px] text-slate-500" data-testid="state-hint">Leave a field empty to use the name composed from your naming governance (shown as placeholder) — override only when needed.</p>
        </div>
      </div>
    </Card>
  );
}

const GUARDRAILS = [
  ["encryption_at_rest", "Encryption at rest", "Storage, databases and buckets are encrypted (SSE / storage_encrypted)."],
  ["no_public_access", "No public access", "Public network access disabled, public buckets prevented, RDS/Cloud SQL private."],
  ["min_tls_1_2", "TLS 1.2 minimum", "min_tls_version / minimum_tls_version pinned to 1.2 on every endpoint."],
  ["https_only", "HTTPS only", "Plain HTTP refused on storage and web apps."],
];

function SecuritySection({ rules, onSaved, readOnly }) {
  const { draft, setDraft, dirty } = useSectionDraft(rules, ["security_guardrails"]);
  const [saving, setSaving] = useState(false);
  const g = draft.security_guardrails || {};
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await client.patch("/governance/rules", { security_guardrails: draft.security_guardrails });
      toast.success("Security guardrails saved");
      onSaved(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };
  const toggle = (k) => !readOnly && setDraft((d) => ({ ...d, security_guardrails: { ...(d.security_guardrails || {}), [k]: !(d.security_guardrails || {})[k] } }));
  return (
    <Card className="rounded-md border border-slate-200 bg-white p-6 shadow-none lg:col-span-2" data-testid="security-section">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold">Security guardrails</h2>
          <p className="mt-1 text-sm text-slate-500">Baked into every generated template, verified by the CI gate and counted in the compliance score.</p>
        </div>
        {!readOnly && <SectionSave dirty={dirty} onSave={save} saving={saving} testid="save-security" />}
      </div>
      <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-2">
        {GUARDRAILS.map(([k, label, hint]) => (
          <button
            key={k}
            type="button"
            onClick={() => toggle(k)}
            data-testid={`guardrail-${k}`}
            data-state={g[k] ? "on" : "off"}
            className={cn(
              "flex items-start gap-3 rounded-md border p-4 text-left transition-colors",
              g[k] ? "border-slate-900 bg-slate-50" : "border-slate-200 opacity-70 hover:border-slate-400",
              readOnly && "cursor-default",
            )}
          >
            <span className={cn("mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full", g[k] ? "bg-slate-900 text-white" : "border border-slate-300 text-transparent")}>
              <CheckCircle2 className="h-3.5 w-3.5" />
            </span>
            <span>
              <span className="block font-display text-sm font-semibold text-slate-900">{label}</span>
              <span className="mt-1 block text-xs text-slate-500">{hint}</span>
            </span>
          </button>
        ))}
      </div>
    </Card>
  );
}

function PolicyExportSection({ rules }) {
  const [files, setFiles] = useState(null);
  const [active, setActive] = useState(null);
  useEffect(() => {
    client.get("/governance/policies").then((r) => {
      setFiles(r.data);
      setActive((a) => a || r.data[1]?.path || r.data[0]?.path);
    });
  }, [rules]);
  const download = async () => {
    const res = await client.get("/governance/policies/download", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "toastern-policies.zip";
    a.click();
    URL.revokeObjectURL(url);
  };
  const groups = useMemo(() => {
    const g = {};
    (files || []).forEach((f) => { const k = f.path.includes("/") ? f.path.split("/")[0] : "root"; (g[k] = g[k] || []).push(f); });
    return g;
  }, [files]);
  const current = files?.find((f) => f.path === active);
  return (
    <Card className="rounded-md border border-slate-200 bg-white p-6 shadow-none lg:col-span-2" data-testid="policy-export-section">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold">Policy-as-Code export</h2>
          <p className="mt-1 text-sm text-slate-500">Your governance as Azure Policy, AWS SCP, GCP Org Policy, OPA Rego and Sentinel — download and apply at the platform level.</p>
        </div>
        <Button onClick={download} className="h-9 rounded-md bg-slate-900 text-white hover:bg-slate-800" data-testid="policies-download">
          <Download className="mr-2 h-4 w-4" /> Download ZIP
        </Button>
      </div>
      {files && (
        <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-[260px_1fr]">
          <div className="space-y-3 self-start lg:sticky lg:top-6" data-testid="policy-file-list">
            {Object.entries(groups).map(([grp, list]) => (
              <div key={grp}>
                <p className="font-mono-tight text-[10px] uppercase tracking-widest text-slate-400">{grp}</p>
                {list.map((f) => (
                  <button
                    key={f.path}
                    type="button"
                    onClick={() => setActive(f.path)}
                    data-testid={`policy-file-${f.path}`}
                    className={cn("block w-full truncate rounded-sm px-2 py-1 text-left font-mono-tight text-xs", active === f.path ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100")}
                  >
                    {f.path.split("/").pop()}
                  </button>
                ))}
              </div>
            ))}
          </div>
          <div className="min-w-0 overflow-hidden rounded-md bg-slate-950" data-testid="policy-preview-wrap">
            <div className="flex items-center justify-between border-b border-slate-800 px-4 py-2">
              <span className="truncate font-mono-tight text-[11px] text-slate-400" data-testid="policy-preview-path">{current?.path}</span>
              <span className="font-mono-tight text-[10px] text-slate-500">{(current?.content || "").split("\n").length} lines</span>
            </div>
            <pre className="overflow-x-auto p-4 font-mono-tight text-[12px] leading-relaxed text-slate-100" data-testid="policy-preview">{current?.content}</pre>
          </div>
        </div>
      )}
    </Card>
  );
}

function EnvironmentsSection({ rules, onSaved, readOnly }) {
  const { draft, setDraft, dirty } = useSectionDraft(rules, ["allowed_environments"]);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await client.patch("/governance/rules", { allowed_environments: draft.allowed_environments });
      toast.success("Environments saved");
      onSaved(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Card className="rounded-md border border-slate-200 bg-white p-6 shadow-none lg:col-span-2">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold">Environments</h2>
          <p className="mt-1 text-sm text-slate-500">The <code className="font-mono-tight">environment</code> tag must be one of these.</p>
        </div>
        {!readOnly && <SectionSave dirty={dirty} onSave={save} saving={saving} testid="save-environments" />}
      </div>
      <div className="mt-6">
        <ListInput
          testid="rule-envs"
          readOnly={readOnly}
          placeholder="e.g. dev"
          value={draft.allowed_environments || []}
          onChange={(list) => setDraft((d) => ({ ...d, allowed_environments: list }))}
        />
      </div>
    </Card>
  );
}

function BudgetsSection({ rules, onSaved, readOnly }) {
  const { draft, setDraft, dirty } = useSectionDraft(rules, ["budgets"]);
  const [saving, setSaving] = useState(false);
  const envs = rules.allowed_environments || [];
  const save = async () => {
    setSaving(true);
    try {
      const clean = Object.fromEntries(Object.entries(draft.budgets || {}).filter(([, v]) => Number(v) > 0).map(([k, v]) => [k, Number(v)]));
      const { data } = await client.patch("/governance/rules", { budgets: clean });
      toast.success("Budgets saved");
      onSaved(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Card className="rounded-md border border-slate-200 bg-white p-6 shadow-none lg:col-span-2" data-testid="budgets-section">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold">Monthly budgets</h2>
          <p className="mt-1 text-sm text-slate-500">Estimated monthly cost ceiling (USD) per environment. The wizard warns when an estimate exceeds it and the project is flagged; generation is never blocked. Leave empty for no limit.</p>
        </div>
        {!readOnly && <SectionSave dirty={dirty} onSave={save} saving={saving} testid="save-budgets" />}
      </div>
      <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3">
        {envs.map((env) => (
          <div key={env}>
            <Label className="font-mono-tight text-[10px] uppercase tracking-widest text-slate-500">{env}</Label>
            <div className="relative mt-1">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">$</span>
              <Input
                type="number"
                min={0}
                readOnly={readOnly}
                data-testid={`budget-${env}`}
                className="h-9 border-slate-200 pl-6 font-mono-tight text-xs"
                placeholder="no limit"
                value={draft.budgets?.[env] ?? ""}
                onChange={(e) => setDraft((d) => ({ ...d, budgets: { ...(d.budgets || {}), [env]: e.target.value } }))}
              />
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function HistoryPanel({ onRolledBack, canRollback }) {
  const [entries, setEntries] = useState([]);
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    client.get("/governance/history?limit=30")
      .then((r) => { setEntries(r.data); setLoaded(true); })
      .catch((err) => { console.warn("Governance history unavailable", err); toast.error("Could not load governance history"); });
  }, []);

  useEffect(() => {
    if (open && !loaded) load();
  }, [open, loaded, load]);

  const rollback = async (entry) => {
    if (!window.confirm("Restore governance rules to this earlier version?")) return;
    setBusyId(entry.id);
    try {
      const { data } = await client.post(`/governance/history/${entry.id}/rollback`);
      toast.success("Rules restored");
      onRolledBack?.(data);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setBusyId(null);
    }
  };

  const fmtValue = (v) => {
    if (v === null || v === undefined) return "—";
    if (Array.isArray(v)) return v.length === 0 ? "[]" : v.join(" · ");
    if (typeof v === "object") return JSON.stringify(v);
    return String(v);
  };

  const fmtDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString();
    } catch { return iso; }
  };

  return (
    <Card className="rounded-md border border-slate-200 bg-white p-6 shadow-none lg:col-span-2" data-testid="history-panel">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between text-left"
        data-testid="history-toggle"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-slate-400" />
          <div>
            <h2 className="font-display text-lg font-semibold">Policy history</h2>
            <p className="mt-1 text-sm text-slate-500">Every change to the workspace governance rules.</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge className="rounded-full bg-slate-100 text-[10px] uppercase tracking-widest text-slate-600" data-testid="history-count">
            {entries.length || (loaded ? 0 : "—")} entries
          </Badge>
          <ChevronDown className={cn("h-4 w-4 text-slate-500 transition-transform", open && "rotate-180")} />
        </div>
      </button>

      {open && (
        <div className="mt-6 space-y-3" data-testid="history-list">
          {entries.length === 0 && (
            <p className="rounded-md border border-dashed border-slate-200 bg-slate-50 p-6 text-center font-mono-tight text-xs text-slate-500">
              No governance changes recorded yet.
            </p>
          )}
          {entries.map((e) => (
            <div key={e.id} className="rounded-md border border-slate-200 bg-white p-4" data-testid={`history-entry-${e.id}`}>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <div className="grid h-7 w-7 place-items-center rounded-full bg-slate-900 text-[10px] font-semibold text-white">
                    {(e.actor_name || e.actor_email || "?").charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-slate-900">{e.actor_name || e.actor_email}</div>
                    <div className="font-mono-tight text-[10px] uppercase tracking-widest text-slate-500">{e.actor_email}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="font-mono-tight text-[11px] text-slate-500">{fmtDate(e.at)}</div>
                  {canRollback && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => rollback(e)}
                      disabled={busyId === e.id}
                      className="h-8 border-slate-300 text-slate-700 hover:border-slate-900 hover:bg-slate-900 hover:text-white"
                      data-testid={`rollback-${e.id}`}
                    >
                      <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> {busyId === e.id ? "Restoring…" : "Restore this version"}
                    </Button>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                {Object.entries(e.changes || {}).map(([field, diff]) => (
                  <div key={field} className="rounded-sm border border-slate-100 bg-slate-50/60 px-3 py-2 text-xs" data-testid={`history-change-${field}`}>
                    <div className="mb-1 font-mono-tight text-[10px] uppercase tracking-widest text-slate-500">{field}</div>
                    <div className="flex items-center gap-2 font-mono-tight text-slate-700">
                      <span className="line-through decoration-red-400 decoration-2">{fmtValue(diff.before)}</span>
                      <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
                      <span className="text-emerald-700">{fmtValue(diff.after)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}


const SECTIONS = [
  { key: "naming", label: "Naming", hint: "pattern · prefix · style", icon: Type, render: (p) => <NamingSection {...p} /> },
  { key: "regions", label: "Regions", hint: "allowed per cloud", icon: Globe2, render: (p) => <RegionsSection {...p} /> },
  { key: "tags", label: "Mandatory tags", hint: "required on every resource", icon: Tags, render: (p) => <TagsSection {...p} /> },
  { key: "environments", label: "Environments", hint: "dev · staging · prod", icon: Layers, render: (p) => <EnvironmentsSection {...p} /> },
  { key: "budgets", label: "Monthly budgets", hint: "alerts in the generator", icon: Wallet, render: (p) => <BudgetsSection {...p} /> },
  { key: "pipeline", label: "Pipeline & state", hint: "CI platform · backend", icon: Workflow, render: (p) => <PipelineSection {...p} /> },
  { key: "security", label: "Security guardrails", hint: "TLS · public access · encryption", icon: ShieldCheck, render: (p) => <SecuritySection {...p} /> },
  { key: "policies", label: "Policy export", hint: "policy-as-code bundle", icon: FileDown, render: ({ rules }) => <PolicyExportSection rules={rules} /> },
  { key: "history", label: "History", hint: "changes & rollback", icon: Clock, render: ({ onSaved, readOnly }) => <HistoryPanel canRollback={!readOnly} onRolledBack={onSaved} /> },
];

function RulesPane({ active, dirtyMap, onSelect }) {
  return (
    <aside className="sticky top-0 flex h-screen w-[300px] min-w-[300px] max-w-[300px] shrink-0 flex-col self-start overflow-hidden border-r border-slate-200 bg-white" data-testid="rules-pane">
      <p className="flex h-16 items-center border-b border-slate-200 px-4 font-mono-tight text-[10px] uppercase tracking-widest text-slate-500">// governance</p>
      <ul className="flex-1 overflow-y-auto p-2">
        {SECTIONS.map((s) => {
          const isActive = s.key === active;
          const isDirty = !!dirtyMap[s.key];
          const Icon = s.icon;
          return (
            <li key={s.key}>
              <button type="button" onClick={() => onSelect(s.key)} data-testid={`rules-pane-${s.key}`} aria-pressed={isActive} data-dirty={isDirty ? "true" : "false"}
                className={cn("flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors", isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100")}>
                <Icon className={cn("h-4 w-4 shrink-0", isActive ? "text-slate-300" : "text-slate-400")} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-display text-sm font-semibold">{s.label}</span>
                  <span className="block truncate font-mono-tight text-[10px] uppercase tracking-widest text-slate-400">{isDirty ? "unsaved changes" : s.hint}</span>
                </span>
                {isDirty && <span className="h-2 w-2 shrink-0 rounded-full bg-amber-400 ring-4 ring-amber-400/20" title="Unsaved changes" data-testid={`rules-dirty-${s.key}`} />}
              </button>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}

function SectionHost({ sectionKey, onDirty, children }) {
  const report = useCallback((dirty) => onDirty(sectionKey, dirty), [sectionKey, onDirty]);
  return <SectionDirtyContext.Provider value={report}>{children}</SectionDirtyContext.Provider>;
}

export default function Rules() {
  const { user } = useAuth();
  const [rules, setRules] = useState(null);
  const [dirtyMap, setDirtyMap] = useState({});
  const [params, setParams] = useSearchParams();
  const readOnly = user?.role !== "admin";
  const active = SECTIONS.some((s) => s.key === params.get("section")) ? params.get("section") : SECTIONS[0].key;
  const current = SECTIONS.find((s) => s.key === active);
  const onDirty = useCallback((key, dirty) => setDirtyMap((m) => (!!m[key] === dirty ? m : { ...m, [key]: dirty })), []);

  useEffect(() => {
    client.get("/governance/rules").then((r) => setRules(r.data));
  }, []);

  if (!rules) return <div className="p-10 text-sm text-slate-500">Loading policies…</div>;

  return (
    <div className="grid grid-cols-[300px_minmax(0,1fr)]" data-testid="rules-layout">
      <RulesPane active={active} dirtyMap={dirtyMap} onSelect={(key) => setParams({ section: key })} />
      <div className="min-w-0 p-10">
        <div className="mb-10">
          <p className="font-mono-tight text-xs uppercase tracking-widest text-slate-500">// policies · {current.label.toLowerCase()}</p>
          <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight" data-testid="page-title">Governance rules</h1>
          <p className="mt-1 text-sm text-slate-600">
            The single set of rules every generated project must satisfy.
            {readOnly && <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 font-mono-tight text-[10px] uppercase tracking-widest text-slate-600">Read only</span>}
            <span className="ml-2 font-mono-tight text-[10px] uppercase tracking-widest text-slate-400">each section saves independently</span>
          </p>
        </div>

        {/* Every section stays mounted (hidden) so unsaved drafts survive switching menus. */}
        {SECTIONS.map((s) => (
          <div key={s.key} className={cn("grid grid-cols-1 gap-6", s.key !== active && "hidden")} data-testid={`rules-section-${s.key}`}>
            <SectionHost sectionKey={s.key} onDirty={onDirty}>{s.render({ rules, onSaved: setRules, readOnly })}</SectionHost>
          </div>
        ))}
      </div>
    </div>
  );
}
